import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import minilibas from "../../assets/images/libasmini.svg";
import logo from "../../assets/images/LibasLogo.svg";
import {
  addReferral,
  auth,
  createUserDocumentFromAuth,
  db,
  doc,
  getUserDataByCode,
  onAuthStateChanged,
  updateDoc,
} from "../../utils/firebase/firebase.utils";
import branch from "branch-sdk";
import { useSelector } from "react-redux";
import { getFunctions, httpsCallable } from "firebase/functions";
const functions = getFunctions();

const COLORS = {
  taupe: "#C6AC96",
  gray: "#808080",
  red: "#E01616",
  darkGray: "#262626",
};

export default function VerifyUsers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("Verifying your account...");
  const [progress, setProgress] = useState(0);
  const { referralcode } = useSelector((store) => store?.flags);

  useEffect(() => {
    // Simulate progress updates for visual feedback
    if (loading) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + 10;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 400);

      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    const verifyAndCompleteRegistration = async () => {
      try {
        setLoading(true);

        // 1. Get pending data from cookies
        const cookies = document.cookie.split(";");
        const pendingUserCookie = cookies.find((c) =>
          c.trim().startsWith("pendingUser=")
        );

        if (!pendingUserCookie) {
          throw new Error(
            "No pending registration found - please sign up again"
          );
        }

        // 2. Parse cookie data
        const cookieValue = pendingUserCookie.split("=")[1];
        const { authUser, userData } = JSON.parse(
          decodeURIComponent(cookieValue)
        );

        // 3. Verify auth state matches cookie data
        const currentUser = auth?.currentUser;
        if (!currentUser || currentUser?.uid !== authUser?.uid) {
          throw new Error("Authentication mismatch - please sign in again");
        }

        // 4. Check email verification status
        if (!currentUser?.emailVerified) {
          throw new Error("Email not verified yet - please check your inbox");
        }
        // 5. Update user verification status in Firestore
        try {
          await updateDoc(doc(db, "users", currentUser?.uid), {
            isVerified: true,
            verifiedAt: new Date().toISOString(),
          });
          console.log("User verification status updated in Firestore");
        } catch (firestoreError) {
          console.error(
            "Failed to update verification status:",
            firestoreError
          );
        }

        // 6. refeeraal
        if (referralcode) {
          var referralcodeuser = await getUserDataByCode(referralcode);

          if (referralcodeuser) {
            addReferral({
              id: authUser?.uid,
              idUser: referralcodeuser,
            });
          }
        }

        // 7. Track signup with Branch
        branch.track("SignUp", { uid: authUser?.uid });

        // 8. Clean up and redirect

        localStorage.removeItem("signupEmail");
        localStorage.removeItem("resendTimer");
        document.cookie =
          "pendingUser=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        setMessage("Account verified successfully!");

        // only send welcome email after verification
        await httpsCallable(
          functions,
          "sendWelcomeEmail"
        )({ sendEmailTo: currentUser?.email });

        setProgress(100);
        setTimeout(() => navigate("/"), 2000);
      } catch (error) {
        console.error("Verification failed:", error);
        setError(error.message);
        // Optionally clear invalid cookie
        document.cookie =
          "pendingUser=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      } finally {
        setLoading(false);
      }
    };

    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        verifyAndCompleteRegistration();
      } else {
        setError("No authenticated user found");
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Custom checkmark path for SVG animation
  const checkVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { type: "spring", duration: 1.5, bounce: 0.3 },
        opacity: { duration: 0.01 },
      },
    },
  };

  // Circle animation
  const circleVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  // Success message animation
  const textVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { delay: 0.7, duration: 0.6 },
    },
  };

  // Celebration particles animation
  const particleVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: (index) => ({
      scale: 1,
      opacity: [0, 1, 0],
      x: Math.random() * 60 - 30,
      y: Math.random() * 60 - 30,
      transition: {
        delay: 0.8 + index * 0.05,
        duration: 0.8,
        ease: "easeOut",
      },
    }),
  };

  return (
    <div
      className="montserrat-font container-fluid min-vh-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: COLORS.darkGray }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="card shadow p-4 mx-auto"
        style={{
          maxWidth: "500px",
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: `1px solid ${COLORS.taupe}`,
          overflow: "hidden",
        }}
      >
        <div className="card-body text-center">
          {/* Logo or Brand Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: loading ? 360 : 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 1,
            }}
            className="mb-4"
          >
            <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto">
              {loading ? (
                <img src={logo} alt="main" />
              ) : error ? (
                <img src={logo} alt="main" />
              ) : (
                <img src={logo} alt="main" />
              )}
            </div>
          </motion.div>
          {/* Status Title */}
          <motion.h2
            className="mb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={{
              color: error ? "#A2191F" : COLORS.darkGray,
              fontSize: "22px",
            }}
          >
            {loading
              ? message
              : error
              ? "Verification Failed"
              : "Account Verified Successfully!"}
          </motion.h2>

          {/* Status Message */}
          <motion.p
            className="text-muted mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{
              color: error ? "#A2191F" : COLORS.gray,
              fontSize: "12px",
            }}
          >
            {loading
              ? "Please wait while we complete your registration."
              : error
              ? error
              : "You will be redirected shortly..."}
          </motion.p>

          {/* Progress Bar */}
          {loading && (
            <motion.div
              className="progress mb-4"
              style={{ height: "8px" }}
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
            >
              <motion.div
                className="progress-bar"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                style={{
                  backgroundColor: COLORS.taupe,
                  height: "100%",
                }}
              ></motion.div>
            </motion.div>
          )}

          {/* Error Button */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <button
                onClick={() => navigate("/")}
                className="btn btn-lg px-4 py-2"
                style={{
                  backgroundColor: COLORS.taupe,
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  fontSize: "14px",
                }}
              >
                Return to libas
              </button>
            </motion.div>
          )}

          {/* Enhanced Success Animation */}
          {!loading && !error && (
            <motion.div
              className="position-relative"
              initial="hidden"
              animate="visible"
              style={{ height: "100px" }}
            >
              {/* Animated circle */}
              <motion.div
                variants={circleVariants}
                className="position-absolute top-50 start-50 translate-middle"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: COLORS.taupe,
                  borderRadius: "50%",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />

              {/* SVG checkmark animation */}
              <motion.svg
                className="position-absolute top-50 start-50 translate-middle"
                width="80"
                height="80"
                viewBox="0 0 80 80"
                initial="hidden"
                animate="visible"
              >
                <motion.path
                  d="M20 40 L35 55 L60 25"
                  fill="transparent"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  variants={checkVariants}
                />
              </motion.svg>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
